import type { SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  output,
} from '@angular/core';
import { FormControl } from '@angular/forms';

import { I18nService, TranslatePipe } from '@recruitee/i18n';
import type { SharingContainerApi } from '@recruitee/sharing-container-types';
import {
  BoxComponent,
  ButtonComponent,
  ButtonLoadingDirective,
  CheckboxComponent,
  DateComponent,
  DialogBodyComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
  DividerComponent,
  EmptyStateComponent,
  GroupComponent,
  HeadingComponent,
  SpinnerComponent,
} from '@recruitee/user-interface';

@Component({
  selector: 'rt-candidate-sharing-assignments',
  templateUrl: './candidate-sharing-assignments.component.html',
  styleUrls: ['./candidate-sharing-assignments.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DialogHeaderComponent,
    DialogBodyComponent,
    BoxComponent,
    SpinnerComponent,
    GroupComponent,
    CheckboxComponent,
    HeadingComponent,
    DateComponent,
    DividerComponent,
    EmptyStateComponent,
    DialogFooterComponent,
    ButtonComponent,
    ButtonLoadingDirective,
    TranslatePipe,
  ],
})
export class CandidateSharingAssignmentsComponent {
  @Input()
  public isLoading: boolean = false;

  @Input() public heading: string = '';
  @Input() public subheading: string = '';
  @Input() public assignments: number[] = [];
  @Input() public containers: SharingContainerApi[] = [];
  @Input() public isLoaded: boolean = false;
  @Input() public submitText?: string;
  @Input() public disableSubmitIfNoAssignments?: boolean = false;
  public cancelClick = output<void>();
  public submitClick = output<number[]>();
  public searchControl = new FormControl<string>('', { nonNullable: true });
  public newAssignments: number[] = [];

  constructor(
    private i18n: I18nService,
    private cdRef: ChangeDetectorRef,
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if ('assignments' in changes && !this.isLoading) {
      this.newAssignments = [...this.assignments];
    }
  }

  public toggleAssignment(container: SharingContainerApi): void {
    if (this.newAssignments.includes(container.id)) {
      this.newAssignments = this.newAssignments.filter(id => id !== container.id);
    } else {
      const containerId = container.id;
      this.newAssignments.push(containerId);
    }
    this.cdRef.detectChanges();
  }

  public containerName(container: SharingContainerApi): string {
    if (container.name == null) {
      const translationKey = 'app.containers.sharing_container_default_name_atc';
      return this.i18n.translate(translationKey, {
        candidatesCount: container.candidatesCount,
      });
    } else {
      return container.name;
    }
  }

  public isSelected(container: SharingContainerApi): boolean {
    return container && this.newAssignments.includes(container.id);
  }

  public isSearching(): boolean {
    return !!this.searchControl.value && this.searchControl.value.length > 0;
  }

  public cancelSearch(): void {
    this.searchControl.reset('');
  }

  public cancel(): void {
    this.cancelClick.emit();
  }

  public submit(): void {
    this.submitClick.emit(this.newAssignments);
  }

  public trackByContainer(index: number, container: SharingContainerApi): number {
    return container.id;
  }
}
